import React, { useEffect, useState, useCallback } from "react";
import { Col, Row, Modal, Button, Card } from "react-bootstrap";
import useApiServices from "../../utils/useApiServices";
import { DateTime } from "luxon";
import swal from "sweetalert";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import {
  FETCH_ALL_MERCHANT_VALIDATIONS,
  INITIATE_TRANSACTION_REPORT,
  GET_ALL_TRANSACTION_REPORTS,
  DOWNLOAD_TRANSACTION_REPORT,
  GET_ALL_SID,
  GET_ALL_VIRTUAL_ACCOUNTS_REPORT,
} from "../../utils/constant";
import Loader from "../Loader/Loader";
import Shimmer from "../../common/Shimmer/Shimmer";
import IconWrapper from "../../common/IconWrapper/IconWrapper";
import { Box } from "@mui/material";
import { convertDateFormat } from "../../utils/helper";
import Cookies from "js-cookie";
import Select from "react-select";

const TransactionsReports = ({ toggleRightBar }) => {
  const token = localStorage.getItem("token") || Cookies.get("access_token");
  const { apiCallFnforGet, apiCallFnforPost } = useApiServices();

  // Table state
  const [archivedData, setArchivedData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [zoomCount, setZoomCount] = useState(16);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  // Form/Modal state
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("");
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [createStartDate, setCreateStartDate] = useState("");
  const [createEndDate, setCreateEndDate] = useState("");
  const [searchOption3, setSearchOption3] = useState("");
  const [searchValue3, setSearchValue3] = useState("");
  const [selectAllMerchants, setSelectAllMerchants] = useState(false);
  const [sids, setSids] = useState([]);
  const [selectedSids, setSelectedSids] = useState([]);
  const [virtualAccounts, setVirtualAccounts] = useState([]);
  const [selectedVirtualAccount, setSelectedVirtualAccount] = useState(null);

  const reportstype = [
    "payin",
    "payout",
    "settlement",
    "withdraw",
  ];

  const fetchTransactionsReport = useCallback(async (page = 0, size = 20) => {
    setTableLoading(true);
    setArchivedData([]); // Clear previous data to ensure only new data is shown
    try {
      const response = await apiCallFnforGet(`${GET_ALL_TRANSACTION_REPORTS}?page=${page}&size=${size}`);
      if (response?.status === "SUCCESS" || response?.statusCode === 200 || response?.statusCode === 202 || response?.statusCode === 0) {
        const mappedData = (response.data || []).map((item, index) => ({
          ...item,
          sNo: (page * size) + index + 1,
          // Generate a unique ID by combining jobId and index to handle potential duplicates from backend
          id: item.jobId ? `${item.jobId}_${index}` : `row_${index}_${page}`,
          reportType: item.reportType?.toUpperCase(),
          statusText: item.status?.toUpperCase(),
          // Metadata from filter
          filterDate: item.requestFilter?.date || "-",
          filterRange: (item.requestFilter?.startDate && item.requestFilter?.endDate)
            ? `${item.requestFilter.startDate} to ${item.requestFilter.endDate}`
            : "-"
        }));
        setArchivedData(mappedData);
        // The user confirmed that 'size' in the response is the total available data
        setRowCount(response?.size || 0);
      } else {
        setArchivedData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching archived reports:", error);
      setArchivedData([]);
      setRowCount(0);
    } finally {
      setTableLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMerchantData = useCallback(async () => {
    try {
      const response = await apiCallFnforGet(FETCH_ALL_MERCHANT_VALIDATIONS);
      if (response?.status === "SUCCESS" || response?.statusCode === 200 || response?.statusCode === 0) {
        const options = response.data.map(m => ({
          value: m.mid,
          label: `${m.mid} - ${m.merchantName}`
        }));
        setMerchants(options);
      } else {
        setMerchants([]);
      }
    } catch (error) {
      setMerchants([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const fetchSids = useCallback(async () => {
    try {
      const response = await apiCallFnforGet(GET_ALL_SID);
      if (response?.status === "SUCCESS" || response?.statusCode === 200 || response?.statusCode === 0) {
        const options = response.data.map(s => ({
          value: s.sid,
          label: s.sid
        }));
        setSids(options);
      } else {
        setSids([]);
      }
    } catch (error) {
      console.error("Error fetching SIDs:", error);
      setSids([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVirtualAccounts = useCallback(async () => {
    try {
      const response = await apiCallFnforGet(GET_ALL_VIRTUAL_ACCOUNTS_REPORT);
      if (response?.status === "SUCCESS" || response?.statusCode === 200 || response?.statusCode === 0) {
        const options = response?.data?.map(v => ({
          value: v,
          label: v
        }));
        setVirtualAccounts(options);
      } else {
        setVirtualAccounts([]);
      }
    } catch (error) {
      console.error("Error fetching virtual accounts:", error);
      setVirtualAccounts([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTransactionsReport(paginationModel.page, paginationModel.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    getMerchantData();
    fetchSids();
    fetchVirtualAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateInputChange = (date) => {
    const inputDateTime = DateTime.fromISO(date, { zone: "UTC" });
    const today = DateTime.now().startOf("day");
    return inputDateTime.toISODate() > today.toISODate();
  };

  const handleFromToDateInputChange = (from, to) => {
    const inputFromDateTime = DateTime.fromISO(from, { zone: "utc" });
    const inputToDateTime = DateTime.fromISO(to, { zone: "utc" });
    if (inputFromDateTime > inputToDateTime) return false;
    if (!inputFromDateTime.isValid || !inputToDateTime.isValid || from === to) return false;
    return true;
  };

  const handleDownloadFile = async (row) => {
    const requestId = row?.jobId;
    const reportTypePart = (row?.reportType || "Report").toLowerCase().replace(/\s+/g, "-");
    const dateTime = DateTime.now().toFormat("yyyyLLdd_HHmmss");
    setLoading(true);
    try {
      const response = await fetch(`${DOWNLOAD_TRANSACTION_REPORT}${requestId}`, {
        method: "GET",
        headers: {
          Authentication: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        if (blob.size > 0) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${reportTypePart}-report_${requestId}_${dateTime}.zip`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          swal({ title: "Success", text: "Report downloaded successfully", icon: "success" });
        } else {
          swal({ title: "Warning", text: "The report file is empty.", icon: "warning" });
        }
      } else {
        const errorText = await response.text();
        swal({ title: "Error", text: errorText || "Failed to download report", icon: "error" });
      }
    } catch (error) {
      swal({ title: "Error", text: "An error occurred while fetching the report.", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateRequest = async () => {
    if (!type) {
      swal({ title: "Warning", text: "Report Type is mandatory.", icon: "warning" });
      return;
    }

    let isDateValid = false;
    if (searchOption3 === "date" && searchValue3) {
      isDateValid = true;
    } else if (searchOption3 === "dateInBetween" && createStartDate && createEndDate) {
      if (!handleFromToDateInputChange(createStartDate, createEndDate) && createStartDate !== createEndDate) {
        swal({ title: "Warning", text: "Start Date can't be greater than End Date!", icon: "warning" });
        return;
      }
      isDateValid = true;
    }

    if (!isDateValid) {
      swal({ title: "Warning", text: "Please select a valid Date or Date Range.", icon: "warning" });
      return;
    }

    if (!selectAllMerchants && selectedMerchants.length === 0) {
      swal({ title: "Warning", text: "Merchant is mandatory (or Select All Merchants).", icon: "warning" });
      return;
    }

    setLoading(true);

    let payload = {
      reportType: type === "withdraw" ? "withdrawal" : type,
      date: searchOption3 === "date" ? searchValue3 : "",
      startDate: searchOption3 === "dateInBetween" ? createStartDate : "",
      endDate: searchOption3 === "dateInBetween" ? createEndDate : "",
      merchantIds: selectAllMerchants ? [] : selectedMerchants.map(m => m.value),
    };

    if (type === "payin") {
      payload.sids = selectedSids.map(s => s.value);
    }
    if (type === "payout") {
      payload.virtualAccountNumber = selectedVirtualAccount?.value || "";
    }

    try {
      const resData = await apiCallFnforPost(INITIATE_TRANSACTION_REPORT, payload);

      if (resData?.status === "SUCCESS" || resData?.statusCode === 200 || resData?.statusCode === 202 || resData?.statusCode === 0) {
        swal({
          title: "Success",
          text: resData?.message || "Report Request Submitted Successfully",
          icon: "success",
        }).then(() => {
          setShowModal(false);
          // Reset Form
          setType("");
          setSelectedMerchants([]);
          setCreateStartDate("");
          setCreateEndDate("");
          setSearchOption3("");
          setSearchValue3("");
          setSelectAllMerchants(false);
          setSelectedSids([]);
          setSelectedVirtualAccount(null);
          if (paginationModel.page === 0 && paginationModel.pageSize === 20) {
            fetchTransactionsReport(0, 20);
          } else {
            setPaginationModel({ page: 0, pageSize: 20 });
          }
        });
      } else {
        swal({ title: "Alert", text: resData?.message || "An error occurred", icon: "error" });
      }
    } catch (error) {
      swal({ title: "Alert", text: error?.response?.data?.message || "An error occurred.", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "sNo", headerName: "S.No", width: 80 },
    { field: "jobId", headerName: "Job ID", minWidth: 280, flex: 1 },
    { field: "reportType", headerName: "Report Type", minWidth: 150, flex: 1 },
    {
      field: "status", headerName: "Status", minWidth: 140, renderCell: (params) => (
        <span className={`badge badge-rounded ${params?.value === 'SUCCESS' ? 'badge-success' : params?.value === 'FAILED' ? 'badge-danger' : 'badge-warning'}`}>
          {params?.value?.toUpperCase()}
        </span>
      )
      , flex: 1
    },
    { field: "filterDate", headerName: "Filter Date", minWidth: 150, flex: 1 },
    { field: "filterRange", headerName: "Date Range", minWidth: 220, flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 200,
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <div className="d-flex align-items-center gap-2 py-1">
          <button
            className="badge badge-rounded badge-success mx-2 px-3 py-2 border-0"
            onClick={() => handleDownloadFile(params?.row)}
            disabled={params.row.status !== "SUCCESS"}
            style={{ opacity: params.row.status === "SUCCESS" ? 1 : 0.5, cursor: params.row.status === "SUCCESS" ? "pointer" : "not-allowed" }}
          >
            Download & View
          </button>
        </div>
      ),
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
        <Box sx={{ flexGrow: 1 }} />
        <div className="d-flex justify-content-start"><GridToolbarQuickFilter /></div>
      </GridToolbarContainer>
    );
  }

  const HandleReset = () => {
    if (paginationModel.page === 0 && paginationModel.pageSize === 20) {
      fetchTransactionsReport(0, 20);
    } else {
      setPaginationModel({ page: 0, pageSize: 20 });
    }
  };

  const incrementZoom = () => {
    if (zoomCount < 28) setZoomCount(zoomCount + 1);
  };

  const decrementZoom = () => {
    if (zoomCount > 14) setZoomCount(zoomCount - 1);
  };

  return (
    <div className="container-fluid">
      {loading && <Loader />}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <section className="admin-dashboard-sec">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className='d-flex align-items-center'>
                    <button
                      type="button"
                      className="btn-close m-3"
                      onClick={toggleRightBar}
                    ></button>
                    <div className="fs-2 text-dark fw-bold">Transactions Reports</div>
                  </div>
                  <div className="col-7 mb-4 mb-sm-0" style={{ textAlign: "right" }}>
                    <IconWrapper title={"Zoomin"}>
                      <span onClick={incrementZoom}>
                        <i className="fa-solid fa-magnifying-glass-plus"></i>{" "}
                      </span>
                    </IconWrapper>
                    <IconWrapper title={"Zoomout"}>
                      <span onClick={decrementZoom}>
                        <i className="fa-solid fa-magnifying-glass-minus"></i>{" "}
                      </span>
                    </IconWrapper>
                    <IconWrapper title={"Refresh"}>
                      <i className="fa fa-refresh" onClick={HandleReset}></i>
                    </IconWrapper>
                    <IconWrapper title={"Request New Report"}>
                      <i className="fa fa-plus-circle text-primary" style={{ cursor: 'pointer' }} onClick={() => setShowModal(true)}></i>
                    </IconWrapper>
                  </div>
                </div>

                <div className="dasboard-view-count-boxes">
                  <Card.Body>
                    <div className="report-grid-view">
                      <div className="material-table-records mb-3" style={{ height: "550px" }}>
                        {tableLoading ? (
                          <Shimmer type="table" />
                        ) : (
                          <DataGrid
                            rows={archivedData}
                            columns={columns}
                            slots={{
                              toolbar: CustomToolbar
                            }}
                            paginationMode="server"
                            rowCount={rowCount}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            pageSizeOptions={[20, 50, 100]}
                            sx={{
                              fontSize: `${zoomCount}px`,
                              "& .MuiDataGrid-columnHeader": {
                                color: "#495057",
                                fontWeight: "700",
                                display: "block"
                              },
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Request Report Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0 pb-0 px-4 pt-4">
          <Modal.Title className="fw-bold text-primary">Generate Transaction Report</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">

          <fieldset className="border p-4 rounded mb-4">
            <legend className="float-none w-auto px-3 fs-6 fw-bold text-muted">Merchant & Report Type</legend>
            <Row className="g-3">
              <Col md={6}>
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <label className="form-label fw-bold small mb-0">Select Merchants</label>
                  <div className="form-check d-flex align-items-center m-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="selectAllMerchantsModal"
                      checked={selectAllMerchants}
                      style={{ width: "16px", height: "16px", cursor: "pointer" }}
                      onChange={(e) => {
                        setSelectAllMerchants(e.target.checked);
                        if (e.target.checked) setSelectedMerchants([]);
                      }}
                    />
                    <label className="form-check-label ms-1 small text-muted mb-0" htmlFor="selectAllMerchantsModal" style={{ cursor: "pointer", fontSize: "11px" }}>
                      Select All
                    </label>
                  </div>
                </div>
                <Select
                  isMulti
                  options={merchants}
                  value={selectedMerchants}
                  onChange={setSelectedMerchants}
                  isDisabled={selectAllMerchants}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="-- Choose Merchants --"
                />
              </Col>

              <Col md={6}>
                <label className="form-label fw-bold small mb-1">Report Category <span className="text-danger">*</span></label>
                <select
                  className="form-control form-select shadow-none"
                  onChange={(e) => setType(e.target.value)}
                  value={type}
                  style={{ height: "38px" }}
                >
                  <option value="">-- Choose Report Type --</option>
                  {reportstype?.map((r) => (
                    <option key={r} value={r}>{r.toUpperCase()} Report</option>
                  ))}
                </select>
              </Col>

              {type === "payin" && (
                <Col md={12}>
                  <label className="form-label fw-bold small mb-1">Select SIDs</label>
                  <Select
                    isMulti
                    options={sids}
                    value={selectedSids}
                    onChange={setSelectedSids}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder="-- Choose SIDs --"
                  />
                </Col>
              )}

              {type === "payout" && (
                <Col md={12}>
                  <label className="form-label fw-bold small mb-1">Select Virtual Account</label>
                  <Select
                    options={virtualAccounts}
                    value={selectedVirtualAccount}
                    onChange={setSelectedVirtualAccount}
                    className="basic-single-select"
                    classNamePrefix="select"
                    placeholder="-- Choose Virtual Account --"
                    isClearable
                  />
                </Col>
              )}
            </Row>
          </fieldset>


          <fieldset className="border p-4 rounded">
            <legend className="float-none w-auto px-3 fs-6 fw-bold text-muted">Time Period</legend>
            <Row className="g-3">
              <Col md={4}>
                <label className="form-label fw-bold small mb-1">Mode <span className="text-danger">*</span></label>
                <select
                  className="form-control form-select shadow-none"
                  value={searchOption3}
                  onChange={(e) => setSearchOption3(e.target.value)}
                >
                  <option value="">Choose Mode</option>
                  <option value="date">Specific Date</option>
                  <option value="dateInBetween">Date Range</option>
                </select>
              </Col>

              <Col md={8}>
                {searchOption3 === "date" && (
                  <div className="form-group">
                    <label className="form-label fw-bold small mb-1">Date</label>
                    <input
                      className="form-control shadow-none"
                      type="date"
                      value={searchValue3}
                      onChange={(e) => {
                        if (handleDateInputChange(e.target.value)) {
                          swal("Warning", "Future dates not allowed", "warning");
                          setSearchValue3("");
                        } else setSearchValue3(e.target.value);
                      }}
                    />
                  </div>
                )}
                {searchOption3 === "dateInBetween" && (
                  <Row className="g-2">
                    <Col md={6}>
                      <label className="form-label fw-bold small mb-1">From</label>
                      <input
                        className="form-control shadow-none"
                        type="date"
                        value={createStartDate}
                        onChange={(e) => {
                          if (handleDateInputChange(e.target.value)) {
                            swal("Warning", "Invalid Date", "warning");
                            setCreateStartDate("");
                          } else setCreateStartDate(e.target.value);
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <label className="form-label fw-bold small mb-1">To</label>
                      <input
                        className="form-control shadow-none"
                        type="date"
                        value={createEndDate}
                        onChange={(e) => {
                          if (handleDateInputChange(e.target.value)) {
                            swal("Warning", "Invalid Date", "warning");
                            setCreateEndDate("");
                          } else setCreateEndDate(e.target.value);
                        }}
                      />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </fieldset>
        </Modal.Body>
        <Modal.Footer className="border-0 px-4 pb-4 pt-0">
          <Button variant="outline-secondary" className="rounded-pill px-4" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="success" className="rounded-pill px-4 shadow" onClick={handleGenerateRequest} disabled={loading}>
            Generate Report Request
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TransactionsReports;
